import { useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'

import UploadIcon from '@mui/icons-material/Upload'
import Button from '@mui/material/Button'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { ddeApi } from '@/service/ddeApi'
import downloadCSV from '@/utils/_legacy/downloadCSV'

import { DialogContent } from './DialogContent'
import {
  createUserUploadHandler,
  type CSVUser,
  EXAMPLE_CSV_DATA,
  EXAMPLE_CSV_HEADERS,
  filterExistingUsers,
  getSaveButtonProps,
  IMPORT_STEPS,
  type ImportStep,
  parseCSVFile,
  validateCSVUsers
} from './ImportUsersCSVButton.helpers'

interface ImportUsersCSVButtonProps {
  operatorId: number
  onSuccess?: () => void
  disabled?: boolean
}

export const ImportUsersCSVButton = ({
  operatorId,
  onSuccess,
  disabled = false
}: ImportUsersCSVButtonProps) => {
  const { enqueueSnackbar } = useSnackbar()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [allUsers, setAllUsers] = useState<{ userId: string }[]>([])
  const [parsedUsers, setParsedUsers] = useState<CSVUser[]>([])
  const [invalidUsers, setInvalidUsers] = useState<CSVUser[]>([])
  const [newUsers, setNewUsers] = useState<CSVUser[]>([])
  const [duplicateUsers, setDuplicateUsers] = useState<CSVUser[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [step, setStep] = useState<ImportStep>(IMPORT_STEPS.UPLOAD)

  useEffect(() => {
    if (isDialogOpen) {
      const fetchAllUsers = async () => {
        try {
          const { data } = await ddeApi.endpoints.users.getAll()
          setAllUsers(data.users.map((user) => ({ userId: user.userId })))
        } catch (error) {
          enqueueSnackbar('Failed to fetch existing users', { variant: 'error' })
        }
      }
      fetchAllUsers()
    }
  }, [isDialogOpen, enqueueSnackbar])

  const resetState = useCallback(() => {
    setParsedUsers([])
    setInvalidUsers([])
    setNewUsers([])
    setDuplicateUsers([])
    setIsUploading(false)
    setStep(IMPORT_STEPS.UPLOAD)
  }, [])

  const handleClose = useCallback(() => {
    setIsDialogOpen(false)
    resetState()
  }, [resetState])

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      try {
        const parsed = await parseCSVFile(file)
        setParsedUsers(parsed)

        const { validUsers: valid, invalidUsers: invalid } = validateCSVUsers(parsed)
        setInvalidUsers(invalid)

        const { newUsers: fresh, existingUsers: duplicates } = filterExistingUsers(valid, allUsers)
        setNewUsers(fresh)
        setDuplicateUsers(duplicates)

        setStep(IMPORT_STEPS.PREVIEW)
      } catch (error) {
        enqueueSnackbar(
          `Error parsing CSV: ${error instanceof Error ? error.message : 'Unknown error'}`,
          {
            variant: 'error'
          }
        )
      }
    },
    [allUsers, enqueueSnackbar]
  )

  const handleUpload = useCallback(async () => {
    if (newUsers.length === 0) {
      enqueueSnackbar('No new users to upload', { variant: 'warning' })
      return
    }

    setIsUploading(true)
    setStep(IMPORT_STEPS.UPLOADING)

    try {
      const uploadHandler = createUserUploadHandler(operatorId, () => onSuccess?.())

      await uploadHandler(newUsers)

      enqueueSnackbar(`Successfully uploaded ${newUsers.length} users`, { variant: 'success' })

      handleClose()
    } catch (error) {
      setStep(IMPORT_STEPS.PREVIEW)

      enqueueSnackbar(
        `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: 'error' }
      )
    } finally {
      setIsUploading(false)
    }
  }, [newUsers, operatorId, onSuccess, enqueueSnackbar, handleClose])

  const handleDownloadExample = useCallback(() => {
    downloadCSV({ filename: 'example_users_import' }, EXAMPLE_CSV_DATA, EXAMPLE_CSV_HEADERS)
  }, [])

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => setIsDialogOpen(true)}
        startIcon={<UploadIcon />}
        disabled={disabled}
      >
        Import CSV
      </Button>

      <ConsoleDialog
        open={isDialogOpen}
        title={`Import Users - Step ${step === IMPORT_STEPS.UPLOAD ? '1' : '2'} of 2`}
        onClose={handleClose}
        dialogSize="sm"
        CloseButtonProps={{
          text: 'Cancel',
          disabled: isUploading
        }}
        SaveButtonProps={getSaveButtonProps(step, newUsers, handleUpload)}
        ActionButtonProps={{
          text: 'Download Example.csv',
          onClick: handleDownloadExample
        }}
        description='All users will be created with the "Operator" role for this operator.'
      >
        <DialogContent
          step={step}
          parsedUsers={parsedUsers}
          newUsers={newUsers}
          duplicateUsers={duplicateUsers}
          invalidUsers={invalidUsers}
          onFileUpload={handleFileUpload}
        />
      </ConsoleDialog>
    </>
  )
}
